import React, { useState, useEffect } from 'react';
import { getStats } from '../../services/reports';
import StatCard from '../../components/Cards/StatCard';
import BarChart from '../../components/Charts/BarChart';
import { 
  Users, Briefcase, Wallet, ClipboardCheck, TrendingUp, 
  Calendar, RefreshCw, LayoutDashboard, ArrowUpRight, 
  ChevronRight, Info, AlertCircle, CheckCircle2
} from 'lucide-react';

const OrgAdminDashboard = () => {
  const [stats, setStats] = useState({
    totalMembers: 0,
    activeCases: 0,
    totalContributions: 0,
    pendingApprovals: 0,
    monthlyContributions: []
  });
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(new Date().toLocaleTimeString());
  const [trendPercent, setTrendPercent] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsRefreshing(true);
      const data = await getStats();
      setStats({
        totalMembers: data?.totalMembers || 0,
        activeCases: data?.activeCases || 0,
        totalContributions: data?.totalContributions || 0,
        pendingApprovals: data?.pendingApprovals || 0,
        monthlyContributions: data?.monthlyContributions || []
      });
      // Calculate trend if we have at least two months
      if (data?.monthlyContributions && data.monthlyContributions.length >= 2) {
        const lastTwo = data.monthlyContributions.slice(-2);
        const prev = lastTwo[0]?.amount || 0;
        const current = lastTwo[1]?.amount || 0;
        if (prev > 0) {
          const change = ((current - prev) / prev) * 100;
          setTrendPercent(change.toFixed(1));
        }
      } else {
        setTrendPercent(null);
      }
      setLastSynced(new Date().toLocaleTimeString());
    } catch (err) {
      console.error("Dashboard Sync Error:", err);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const getSystemStatus = () => {
    const pending = stats.pendingApprovals;
    if (pending > 10) return { 
      level: 'critical',
      color: 'text-rose-500', 
      bg: 'bg-rose-950',
      accent: 'bg-rose-500',
      msg: 'Critical: Immediate approval required.',
      icon: <AlertCircle className="text-rose-400" size={24} />
    };
    if (pending > 0) return { 
      level: 'warning',
      color: 'text-amber-500', 
      bg: 'bg-slate-900', 
      accent: 'bg-amber-500',
      msg: 'Action Required: Pending reviews.',
      icon: <AlertCircle className="text-amber-400" size={24} />
    };
    return { 
      level: 'healthy',
      color: 'text-emerald-500', 
      bg: 'bg-slate-900', 
      accent: 'bg-emerald-500',
      msg: 'System Healthy: All cases cleared.',
      icon: <CheckCircle2 className="text-emerald-400" size={24} />
    };
  };

  const status = getSystemStatus();
  const currentYear = new Date().getFullYear();

  return (
    <div className="p-4 md:p-10 max-w-[1600px] mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      
      {/* HEADER */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 bg-white p-6 rounded-[2rem] border border-slate-100 shadow-sm">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <LayoutDashboard size={18} className="text-indigo-600" />
            </div>
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Admin Command Center</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">Organization Overview</h1>
          <div className="flex items-center gap-2 text-slate-400 text-xs font-medium">
            <span className={`flex h-2 w-2 rounded-full animate-pulse ${status.accent}`} />
            System {status.level === 'healthy' ? 'Optimized' : 'Needs Attention'} • Last synced {lastSynced}
          </div>
        </div>

        <button 
          onClick={loadData}
          disabled={isRefreshing}
          className="group flex items-center gap-2 bg-slate-900 text-white px-6 py-3.5 rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-lg active:scale-95 disabled:opacity-50"
        >
          <RefreshCw size={18} className={isRefreshing ? 'animate-spin' : ''} />
          <span className="text-sm">Refresh Data</span>
        </button>
      </div>

      {/* STATS GRID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard 
          title="Total Members" 
          value={stats.totalMembers} 
          icon={<Users size={22} />}
          trend={trendPercent !== null ? `+${trendPercent}% vs last month` : "This month"}
          variant="indigo"
        />
        <StatCard 
          title="Active Cases" 
          value={stats.activeCases} 
          icon={<Briefcase size={22} />}
          trend="Currently in-progress"
          variant="indigo" 
        />
        <StatCard 
          title="Total Contributions" 
          value={`KSh ${Number(stats.totalContributions).toLocaleString()}`} 
          icon={<Wallet size={22} />}
          trend={trendPercent !== null ? `${trendPercent >= 0 ? '↑' : '↓'} ${Math.abs(trendPercent)}%` : "Trending"}
          variant="green"
        />
        <StatCard 
          title="Critical Tasks" 
          value={stats.pendingApprovals} 
          icon={<ClipboardCheck size={22} />}
          trend={stats.pendingApprovals > 0 ? "Urgent Action Required" : "Up to date"}
          variant={stats.pendingApprovals > 10 ? "red" : stats.pendingApprovals > 0 ? "orange" : "indigo"}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* CHART SECTION */}
        <div className="lg:col-span-8 bg-white p-8 rounded-[2.5rem] border border-slate-200 shadow-xl shadow-slate-100/40 relative overflow-hidden group">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-10 gap-4">
            <div className="space-y-1">
              <h2 className="text-2xl font-black text-slate-900">Monthly Contributions</h2>
              <p className="text-slate-400 text-sm font-medium">
                {stats.monthlyContributions.length ? `${currentYear} trend` : 'No data available'}
              </p>
            </div>
          </div>
          
          <div className="h-[380px] w-full">
            {stats.monthlyContributions && stats.monthlyContributions.length > 0 ? (
              <BarChart 
                data={stats.monthlyContributions} 
                xKey="month" 
                barKeys={['amount']} 
                colors={['#4f46e5']} 
              />
            ) : (
              <div className="flex items-center justify-center h-full text-slate-400">
                <p>No contribution data yet.</p>
              </div>
            )}
          </div>
        </div>

        {/* OPERATIONS INTELLIGENCE */}
        <div className="lg:col-span-4">
          <div className={`${status.bg} rounded-[2.5rem] p-8 text-white flex flex-col h-full relative overflow-hidden shadow-2xl transition-colors duration-500`}>
            <div className="relative z-10 flex flex-col h-full">
              <div className="flex justify-between items-start mb-6">
                <div className="p-3 bg-white/10 rounded-2xl border border-white/10 backdrop-blur-md">
                  {status.icon}
                </div>
              </div>

              <h3 className="text-2xl font-bold leading-tight mb-4">Operations<br/>Intelligence</h3>
              
              <div className="space-y-4 flex-grow">
                <p className="text-slate-300 text-sm leading-relaxed">
                  {status.level === 'critical' 
                    ? "Approval pipeline is critically backed up. This may delay welfare disbursements." 
                    : status.level === 'warning'
                    ? "Pending items require attention. Action is recommended to maintain efficiency."
                    : "System performance is optimal. All metrics are within expected thresholds."}
                </p>
                
                <div className="py-6 border-y border-white/5 space-y-4">
                  <div className="flex justify-between text-[10px] uppercase font-black tracking-widest">
                    <span className="text-slate-500">Pipeline Status</span>
                    <span className={status.color}>{status.msg}</span>
                  </div>
                  
                  <div className="w-full bg-white/5 h-2 rounded-full overflow-hidden">
                    <div 
                      className={`${status.accent} h-full rounded-full transition-all duration-1000`} 
                      style={{ width: `${Math.min((stats.pendingApprovals / 15) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              </div>

              <button className={`mt-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg ${
                status.level === 'critical' 
                ? 'bg-rose-500 text-white hover:bg-rose-600 shadow-rose-900/20' 
                : 'bg-white text-slate-900 hover:bg-slate-50'
              }`}>
                {status.level === 'healthy' ? 'View All Logs' : 'Resolve Pending Items'}
                <ChevronRight size={14} />
              </button>
            </div>
            
            <div className={`absolute top-0 right-0 -mr-16 -mt-16 w-64 h-64 rounded-full blur-[80px] opacity-40 ${status.accent}`} />
          </div>
        </div>

      </div>
    </div>
  );
};

export default OrgAdminDashboard;